import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api, { API } from "@/lib/api";
import { FolderOpen, FolderPlus, Trash2, ExternalLink, Gavel, Sparkles, Copy, FileDown, Save, Scale, Share2, Users, X, FileText, Pencil, Check, Folder } from "lucide-react";
import { toast } from "sonner";

export default function Research() {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [courses, setCourses] = useState({});
  const [notes, setNotes] = useState({});
  const [myGroups, setMyGroups] = useState([]);
  const [shareFor, setShareFor] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all"); // all | none | folderId
  const [newFolder, setNewFolder] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const load = () => api.get("/bookmarks").then((r) => {
    setBookmarks(r.data);
    setNotes(Object.fromEntries(r.data.map((b) => [b.id, b.note || ""])));
  });
  const loadFolders = () => api.get("/folders").then((r) => setFolders(r.data)).catch(() => {});

  useEffect(() => {
    load();
    loadFolders();
    api.get("/courses").then((r) => {
      const map = {};
      r.data.forEach((c) => (map[c.slug] = c.title));
      setCourses(map);
    });
    api.get("/community/groups").then((r) => setMyGroups((r.data || []).filter((g) => g.member))).catch(() => {});
  }, []);

  const createFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    await api.post("/folders", { name });
    setNewFolder("");
    toast.success(`Folder “${name}” created`);
    loadFolders();
  };
  const renameFolder = async (id) => {
    const name = renameVal.trim();
    if (!name) return;
    await api.patch(`/folders/${id}`, { name });
    setRenaming(null);
    toast.success("Folder renamed");
    loadFolders();
  };
  const deleteFolder = async (id) => {
    await api.delete(`/folders/${id}`);
    if (activeFolder === id) setActiveFolder("all");
    toast.success("Folder deleted");
    loadFolders(); load();
  };
  const moveTo = async (b, folderId) => {
    await api.patch(`/bookmarks/${b.id}`, { folder_id: folderId || null });
    toast.success(folderId ? "Moved to folder" : "Removed from folder");
    load(); loadFolders();
  };

  const shareToGroup = async (b, g) => {
    const parts = [b.title];
    if (b.citation) parts.push(b.citation);
    if (b.content) parts.push("\n" + b.content);
    if (notes[b.id]) parts.push("\nMy note: " + notes[b.id]);
    if (b.url) parts.push("\n" + b.url);
    parts.push("\n— Shared from my Research folder");
    try {
      await api.post("/community/posts", { group_id: g.id, content: parts.join("\n") });
      toast.success(`Shared to ${g.name}`);
      setShareFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not share"); }
  };

  const remove = async (id) => { await api.delete(`/bookmarks/${id}`); toast.success("Removed"); load(); loadFolders(); };
  const saveNote = async (id) => { await api.patch(`/bookmarks/${id}`, { note: notes[id] || "" }); toast.success("Note saved"); };
  const editDoc = (b) => navigate("/templates", { state: { doc: b } });

  const exportDoc = async (course) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/research/export${course ? `?course=${course}` : ""}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `research-${course || "all"}.docx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to Word");
    } catch { toast.error("Export failed"); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  const visible = bookmarks.filter((b) => {
    if (activeFolder === "all") return true;
    if (activeFolder === "none") return !b.folder_id;
    return b.folder_id === activeFolder;
  });

  const FolderTab = ({ id, label, count }) => (
    <button onClick={() => setActiveFolder(id)} data-testid={`folder-tab-${id}`}
      className={`px-3.5 py-2 text-xs uppercase tracking-widest font-mono border transition-colors whitespace-nowrap ${activeFolder === id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}>
      {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-7 h-7 text-primary" />
            <h1 className="font-serif text-5xl">Research Folders</h1>
          </div>
          {bookmarks.length > 0 && (
            <button onClick={() => exportDoc(null)} data-testid="export-all"
              className="flex items-center gap-2 border border-border px-4 py-2.5 text-xs uppercase tracking-widest font-mono text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <FileDown className="w-4 h-4" /> Export all
            </button>
          )}
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Cases, statutes, AI answers and filled documents you've saved. Organize them into named folders, add notes,
          re-open saved documents to edit, and export to Word.
        </p>

        {/* Folder bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="folder-bar">
          <FolderTab id="all" label="All" count={bookmarks.length} />
          <FolderTab id="none" label="Unfiled" count={bookmarks.filter((b) => !b.folder_id).length} />
          {folders.map((f) => (
            <div key={f.id} className="flex items-center">
              {renaming === f.id ? (
                <div className="flex items-center gap-1 bg-card border border-primary px-2 py-1 rounded-sm">
                  <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && renameFolder(f.id)}
                    className="bg-transparent text-sm outline-none w-28" data-testid={`folder-rename-input-${f.id}`} />
                  <button onClick={() => renameFolder(f.id)} data-testid={`folder-rename-save-${f.id}`} className="text-primary"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setRenaming(null)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="flex items-center">
                  <FolderTab id={f.id} label={f.name} count={f.count} />
                  {activeFolder === f.id && (
                    <div className="flex items-center gap-1 ml-1">
                      <button onClick={() => { setRenaming(f.id); setRenameVal(f.name); }} data-testid={`folder-rename-${f.id}`}
                        title="Rename" className="text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteFolder(f.id)} data-testid={`folder-delete-${f.id}`}
                        title="Delete folder" className="text-muted-foreground hover:text-destructive-foreground"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center gap-1 ml-1">
            <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createFolder()}
              placeholder="New folder…" data-testid="new-folder-input"
              className="bg-muted border border-input px-2.5 py-2 rounded-sm text-sm outline-none focus:border-primary w-32" />
            <button onClick={createFolder} data-testid="new-folder-create"
              className="flex items-center gap-1.5 border border-border px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-mono text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <FolderPlus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>

        {bookmarks.length === 0 && (
          <div className="border border-border bg-card p-12 text-center text-muted-foreground">
            Nothing saved yet. Use the <span className="text-primary">Save</span> buttons on Case Law, Statutes, or the{" "}
            <Link to="/templates" className="text-primary underline">Templates</Link> page.
          </div>
        )}

        <div className="space-y-3">
          {visible.map((b) => (
            <div key={b.id} className="bg-card border border-border p-5" data-testid={`research-item-${b.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  {b.kind === "answer" ? <Sparkles className="w-4 h-4 text-primary mt-1 shrink-0" /> :
                   b.kind === "statute" ? <Scale className="w-4 h-4 text-primary mt-1 shrink-0" /> :
                   b.kind === "document" ? <FileText className="w-4 h-4 text-primary mt-1 shrink-0" /> :
                   <Gavel className="w-4 h-4 text-primary mt-1 shrink-0" />}
                  <div>
                    <div className="font-serif text-lg leading-tight">{b.title}</div>
                    {b.citation && <div className="text-xs font-mono text-muted-foreground">{b.citation}</div>}
                    {b.course_slug && courses[b.course_slug] && <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mt-0.5">{courses[b.course_slug]}</div>}
                    {b.content && <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-4 whitespace-pre-wrap">{b.content}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {b.kind === "document" && (
                    <button onClick={() => editDoc(b)} data-testid={`research-edit-${b.id}`} title="Edit document" className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  )}
                  {b.content && <button onClick={() => copy(b.content)} className="text-muted-foreground hover:text-primary" title="Copy"><Copy className="w-4 h-4" /></button>}
                  {b.url && <a href={b.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><ExternalLink className="w-4 h-4" /></a>}
                  <div className="relative">
                    <button onClick={() => setShareFor(shareFor === b.id ? null : b.id)} data-testid={`research-share-${b.id}`}
                      title="Share to a group" className={`${shareFor === b.id ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                      <Share2 className="w-4 h-4" />
                    </button>
                    {shareFor === b.id && (
                      <div className="absolute right-0 top-6 z-30 w-60 bg-card border border-border rounded-sm shadow-2xl p-2" data-testid={`research-share-menu-${b.id}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-1.5 pb-1.5 mb-1.5 border-b border-border">
                          <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-primary" /> Share to group</span>
                          <button onClick={() => setShareFor(null)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        {myGroups.length === 0 ? (
                          <p className="text-xs text-muted-foreground px-1.5 py-1">Join a group first in the <Link to="/community" className="text-primary underline">Community</Link>.</p>
                        ) : (
                          <div className="max-h-52 overflow-y-auto flex flex-col">
                            {myGroups.map((g) => (
                              <button key={g.id} onClick={() => shareToGroup(b, g)} data-testid={`research-share-to-${g.id}`}
                                className="text-left text-sm px-2 py-1.5 rounded-sm hover:bg-muted flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-primary shrink-0" /><span className="truncate">{g.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(b.id)} data-testid={`research-remove-${b.id}`} className="text-muted-foreground hover:text-destructive-foreground"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-4 pl-7 flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <textarea data-testid={`research-note-${b.id}`} value={notes[b.id] || ""}
                    onChange={(e) => setNotes({ ...notes, [b.id]: e.target.value })}
                    placeholder="Add your own analysis / notes…" rows={2}
                    className="w-full bg-muted border border-input px-3 py-2 rounded-sm outline-none focus:border-primary text-sm" />
                  <button onClick={() => saveNote(b.id)} data-testid={`research-note-save-${b.id}`}
                    className="mt-2 flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-muted-foreground hover:text-primary transition-colors">
                    <Save className="w-3.5 h-3.5" /> Save note
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Folder className="w-3.5 h-3.5 text-muted-foreground" />
                  <select value={b.folder_id || ""} onChange={(e) => moveTo(b, e.target.value)} data-testid={`research-move-${b.id}`}
                    className="bg-muted border border-input px-2 py-1.5 rounded-sm text-sm outline-none focus:border-primary">
                    <option value="">Unfiled</option>
                    {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
          {bookmarks.length > 0 && visible.length === 0 && (
            <p className="text-muted-foreground text-sm">This folder is empty. Move items here using the folder dropdown on any item.</p>
          )}
        </div>
      </div>
    </PageShell>
  );
}
